import { Slide, Stack, Typography, useTheme } from "@mui/material";
import { Bot, CheckCheck, User } from "lucide-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import {
  AvatarContainer,
  Container,
  MessageBox,
  MessageContainer,
  MessageMeta,
  RateExperienceRow,
  SuggestedQuestionChip,
  SuggestedQuestionsRow, 
  Timestamp
} from "./AISupportChatMessageBox.styles";
import ChatAgentFeedbackDialog from "./ChatAgentFeedbackDialog.component";
import { ButtonDigited } from "../../../../components/Button/Button.variants";
import { ChatAgentMessage, FeedbackPayload } from "../../AISupportChat.types";
import AISupportChatSpinner from "../AISupportChatSpinner/AISupportChatSpinner.component";

interface AISupportChatMessageBoxProps {
  index: number;
  message: ChatAgentMessage; 
  messagesList: ChatAgentMessage[]; 
  onSuggestionClick?: (question: string) => void;
  showRateExperience: boolean,
  submitFeedback: (payload: FeedbackPayload, feedbackTargetIndex: number | null, messages: ChatAgentMessage[]) => Promise<void>,
}

const AISupportChatMessageBox = ({
  index,
  message,
  messagesList,
  onSuggestionClick,
  showRateExperience,
  submitFeedback
}: AISupportChatMessageBoxProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isAI = message.sender === "assistant";
  const hasContent = message.response !== "";
  const suggestedQuestions = message.suggested_questions ?? [];
  const [feedbackTargetIndex, setFeedbackTargetIndex] = useState<number | null>(null);

  const MessageAvatar = () => {
    return (
      <>
        { isAI ? (
          <Bot aria-label={ "Avatar" } color={ theme.customColors.textPrimaryCta } size={ 24 } />
        ) : (
          <User aria-label={ "Avatar" } color={ theme.customColors.textWidget } size={ 24 } />
        ) }
      </>
    );
  };

  return (
    <Slide direction={ "up" } in mountOnEnter unmountOnExit>
      <Stack
        paddingX={ "3rem" }
      >
        <Container isAI={ isAI } messageIndex={ index } gap={ "10px" }>
          <AvatarContainer theme={ theme } isAI={ isAI }>
            <MessageAvatar />
          </AvatarContainer>
          <Stack>
            <MessageContainer theme={ theme } isAI={ isAI }>
              { !hasContent ? (
                <AISupportChatSpinner />
              ) : (
                <MessageBox theme={ theme } isAI={ isAI }>
                  <ReactMarkdown>{ message.response }</ReactMarkdown>
                </MessageBox>
              ) }
            </MessageContainer>
            <MessageMeta isAI={ isAI }>
              { message.timestamp && <Timestamp>{ message.timestamp }</Timestamp> }
              { !isAI && (
                <CheckCheck  size={ "0.875rem" }  />
              ) }
              { isAI && hasContent && showRateExperience && (
                <RateExperienceRow>
                  <Timestamp>{ t("rateExperiencePrompt") }</Timestamp>
                  <ButtonDigited 
                    typology="tertiary"
                    onClick={ () => setFeedbackTargetIndex(index) }
                    sx={ {
                      textDecoration: "none !important"
                    
                    } }
                    text={ 
                      <Typography 
                        sx={ {
                          fontSize: "0.75rem",
                          fontWeight:"600"
                        } }>
                        {  t("rateExperience") } 
                      </Typography>
                    }
                  />
                </RateExperienceRow>
              ) }
            </MessageMeta>
          </Stack>
          <ChatAgentFeedbackDialog
            open={ feedbackTargetIndex !== null }
            onClose={ () => setFeedbackTargetIndex(null) }
            onSubmit={ (payload)=> submitFeedback(payload, feedbackTargetIndex, messagesList) }
            // agentName={ t("title") || undefined }
          />
        </Container>

        { isAI && hasContent && suggestedQuestions.length > 0 && (
          <SuggestedQuestionsRow>
            { suggestedQuestions.map((question, questionIndex) => (
              <SuggestedQuestionChip
                key={ `sugg_${index}_${questionIndex}` }
                chipIndex={ questionIndex }
                onClick={ () => onSuggestionClick?.(question) }
                disabled={ !onSuggestionClick }
              >
                { question }
              </SuggestedQuestionChip>
            )) }
          </SuggestedQuestionsRow>
        ) }
      </Stack>
    </Slide>
  );
};

export default AISupportChatMessageBox;
